const routes = window.Shopify?.routes?.root ?? '/';

function publishCart(sections) {
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: { sections } }));
}

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-close]')) this.close();

      const remove = event.target.closest('[data-drawer-remove]');
      if (remove) {
        event.preventDefault();
        this.removeLine(remove);
      }
    });
    this.onKeydown = (event) => {
      if (event.key === 'Escape' && !this.hidden) this.close();
    };
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('cart:updated', (event) => this.render(event.detail.sections));
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
  }

  open() {
    this.hidden = false;
    document.body.classList.add('drawer-open');
    this.querySelector('[data-close]')?.focus();
  }

  close() {
    this.hidden = true;
    document.body.classList.remove('drawer-open');
  }

  async removeLine(link) {
    if (this.hasAttribute('aria-busy')) return;
    this.setAttribute('aria-busy', 'true');

    const onCartPage = Boolean(document.querySelector('cart-form'));
    const sections = onCartPage ? ['cart-drawer', 'main'] : ['cart-drawer'];

    try {
      const response = await fetch(`${routes}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: link.dataset.key,
          quantity: 0,
          sections,
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) throw new Error(response.status);

      const cart = await response.json();
      publishCart(cart.sections);
    } catch (error) {
      console.error(error);
      window.location.href = link.href;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  render(sections) {
    const markup = sections?.['cart-drawer'];
    if (!markup) return;
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const panel = parsed.querySelector('.cart-drawer__panel');
    if (panel) this.querySelector('.cart-drawer__panel')?.replaceWith(panel);
  }
}

class CartToggle extends HTMLElement {
  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', () => {
      document.getElementById('cart-drawer')?.open();
    });
    document.addEventListener('cart:updated', (event) => this.updateCount(event.detail.sections));
  }

  updateCount(sections) {
    const markup = sections?.['cart-drawer'];
    if (!markup) return;
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const count = parsed.querySelector('[data-cart-total]')?.dataset.cartTotal;
    if (count === undefined) return;

    const badge = this.querySelector('[data-cart-count]');
    badge.textContent = count;
    badge.hidden = count === '0';
  }
}

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[type="submit"]');
    this.error = this.querySelector('[data-form-error]');
    this.form?.addEventListener('submit', (event) => this.submit(event));
  }

  async exceedsStock() {
    const max = Number(this.dataset.max || 0);
    const variantId = this.form?.querySelector('[name="id"]')?.value;
    if (!max || !variantId) return false;

    const wanted = Number(this.form.querySelector('[name="quantity"]')?.value || 1);

    let inCart = 0;
    try {
      const cart = await fetch(`${routes}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      inCart = (cart.items || [])
        .filter((item) => String(item.variant_id) === String(variantId))
        .reduce((total, item) => total + item.quantity, 0);
    } catch {
      return false;
    }

    if (inCart + wanted <= max) return false;

    this.error.textContent = this.dataset.limitText?.replace('[count]', max) || '';
    return true;
  }

  async submit(event) {
    event.preventDefault();
    this.error.textContent = '';
    this.button.setAttribute('aria-busy', 'true');

    if (await this.exceedsStock()) {
      this.button.removeAttribute('aria-busy');
      return;
    }

    const body = new FormData(this.form);
    body.append('sections', 'cart-drawer');

    try {
      const response = await fetch(`${routes}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });
      const result = await response.json();
      if (result.status) throw new Error(result.description);

      publishCart(result.sections);
      document.getElementById('cart-drawer')?.open();
    } catch (error) {
      this.error.textContent = error.message;
    } finally {
      this.button.removeAttribute('aria-busy');
    }
  }
}

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[type="application/json"]').textContent);
    this.addEventListener('change', () => this.update());
  }

  get selectedOptions() {
    return Array.from(this.querySelectorAll('input:checked'), (input) => input.value);
  }

  update() {
    const selected = this.selectedOptions;
    const match = this.variants.find((variant) =>
      variant.options.every((option, index) => option === selected[index])
    );

    const input = document.querySelector('[name="id"]');
    const button = document.querySelector('[data-add-to-cart]');

    if (!match) {
      button.disabled = true;
      return;
    }

    input.value = match.id;
    button.disabled = !match.available;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', match.id);
    window.history.replaceState({}, '', url);

    this.dispatchEvent(new CustomEvent('variant:changed', { bubbles: true, detail: { variant: match } }));
  }
}

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', () => {
        const max = Number(this.input.dataset.max || 0);

        if (button.dataset.step === 'up') {
          if (max > 0 && Number(this.input.value) >= max) {
            this.warn(max);
            return;
          }
          this.input.stepUp();
        } else {
          this.input.stepDown();
        }

        this.warn(0);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  get notice() {
    return (
      this.closest('cart-form')?.querySelector('[data-cart-notice]') ||
      this.closest('product-form')?.querySelector('[data-form-error]')
    );
  }

  warn(max) {
    const notice = this.notice;
    if (!notice) return;

    if (!max) {
      notice.textContent = '';
      notice.hidden = true;
      return;
    }

    notice.textContent = this.dataset.limitText?.replace('[count]', max) || '';
    notice.hidden = false;
  }
}

class MenuToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.drawer = document.getElementById(this.button.getAttribute('aria-controls'));
    this.root = this.drawer.querySelector('.mobile-nav__panel');
    this.search = this.drawer.querySelector('.mobile-nav__search');
    this.searchButton = this.drawer.querySelector('[data-search-toggle]');
    this.trail = [];
    this.scrollMemo = new Map();

    this.button.addEventListener('click', () => (this.drawer.hidden ? this.open() : this.close()));
    this.drawer.addEventListener('click', (event) => this.route(event));

    this.onKeydown = (event) => {
      if (event.key === 'Escape' && !this.drawer.hidden) this.close();
    };
    document.addEventListener('keydown', this.onKeydown);

    this.desktop = window.matchMedia('(min-width: 900px)');
    this.onResize = () => {
      if (this.desktop.matches && !this.drawer.hidden) this.close();
    };
    this.desktop.addEventListener('change', this.onResize);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    this.desktop.removeEventListener('change', this.onResize);
  }

  route(event) {
    if (event.target.closest('[data-menu-close]')) {
      this.close();
      return;
    }

    if (event.target.closest('[data-search-toggle]')) {
      this.toggleSearch();
      return;
    }

    if (event.target.closest('cart-toggle')) {
      this.close({ keepLock: true });
      return;
    }

    const open = event.target.closest('[data-panel-open]');
    if (open) {
      this.push(open.nextElementSibling);
      return;
    }

    if (event.target.closest('[data-panel-back]')) this.pop();
  }

  open() {
    this.drawer.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    document.body.classList.add('drawer-open');
    this.drawer.querySelector('[data-menu-close]')?.focus();
  }

  close({ keepLock = false } = {}) {
    this.drawer.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    if (!keepLock) document.body.classList.remove('drawer-open');
    this.reset();
  }

  reset() {
    this.trail.forEach((panel) => panel.classList.remove('is-active', 'is-parent'));
    this.trail = [];
    this.root.classList.add('is-active');
    this.root.classList.remove('is-parent');
    this.closeSearch();
  }

  push(panel) {
    if (!panel) return;
    const parent = this.current();
    this.scrollMemo.set(parent, parent.scrollTop);
    parent.scrollTop = 0;
    parent.classList.replace('is-active', 'is-parent');
    panel.classList.add('is-active');
    panel.scrollTop = 0;
    this.trail.push(panel);
    panel.querySelector('[data-panel-back]')?.focus();
  }

  pop() {
    const panel = this.trail.pop();
    if (!panel) return;
    panel.classList.remove('is-active');
    const parent = this.current();
    parent.classList.replace('is-parent', 'is-active');
    parent.scrollTop = this.scrollMemo.get(parent) || 0;
    parent.querySelector('[data-panel-open], [data-panel-back]')?.focus();
  }

  current() {
    return this.trail[this.trail.length - 1] || this.root;
  }

  toggleSearch() {
    if (!this.search) return;
    if (this.search.hidden) {
      this.search.hidden = false;
      this.searchButton.setAttribute('aria-expanded', 'true');
      this.search.querySelector('input')?.focus();
    } else {
      this.closeSearch();
    }
  }

  closeSearch() {
    if (!this.search) return;
    this.search.hidden = true;
    this.searchButton?.setAttribute('aria-expanded', 'false');
  }
}

class CartForm extends HTMLElement {
  connectedCallback() {
    this.section = this.dataset.section;

    this.addEventListener('change', (event) => {
      const input = event.target.closest('[data-quantity]');
      if (!input) return;

      let wanted = Math.max(0, Number(input.value) || 0);
      const max = Number(input.dataset.max || 0);

      if (max > 0 && wanted > max) {
        wanted = max;
        input.value = max;
        this.notify(this.dataset.limitText?.replace('[count]', max));
      }

      this.change(input.dataset.key, wanted);
    });

    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-remove]');
      if (!remove) return;
      event.preventDefault();
      this.change(remove.dataset.key, 0);
    });

    this.onCartUpdated = (event) => {
      if (this.updating) return;
      const markup = event.detail.sections?.[this.section];
      if (markup) this.render(markup);
    };
    document.addEventListener('cart:updated', this.onCartUpdated);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.onCartUpdated);
  }

  async change(key, quantity) {
    if (!key) return;
    this.updating = true;
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${routes}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: [this.section, 'cart-drawer'].filter(Boolean),
          sections_url: window.location.pathname,
        }),
      });

      const cart = await response.json();
      if (cart.status) throw new Error(cart.description || cart.message);

      publishCart(cart.sections);
      this.render(cart.sections?.[this.section]);

      const line = cart.items?.find((item) => item.key === key);
      if (quantity > 0 && line && line.quantity < quantity) {
        this.notify(this.dataset.limitText?.replace('[count]', line.quantity));
      }
    } catch (error) {
      console.error(error);
      window.location.reload();
    } finally {
      this.updating = false;
      this.removeAttribute('aria-busy');
    }
  }

  notify(message) {
    if (!message) return;
    const notice = this.querySelector('[data-cart-notice]');
    if (!notice) return;
    notice.textContent = message;
    notice.hidden = false;
  }

  render(markup) {
    if (!markup) {
      window.location.reload();
      return;
    }

    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const next = parsed.querySelector('cart-form');

    if (!next) {
      window.location.reload();
      return;
    }

    const active = document.activeElement;
    const key = active?.dataset?.key;

    this.innerHTML = next.innerHTML;

    if (key) {
      const restored = this.querySelector(`[data-quantity][data-key="${key}"]`);
      restored?.focus();
    }
  }
}

function showPageLoader() {
  document.querySelector('[data-page-loader]')?.setAttribute('aria-busy', 'true');
}

const FACET_SCROLL_KEY = 'facet-scroll';

function keepScrollPosition() {
  sessionStorage.setItem(FACET_SCROLL_KEY, JSON.stringify({ path: window.location.pathname, y: window.scrollY }));
}

function restoreScrollPosition() {
  const saved = JSON.parse(sessionStorage.getItem(FACET_SCROLL_KEY) || 'null');
  sessionStorage.removeItem(FACET_SCROLL_KEY);
  if (saved?.path === window.location.pathname) window.scrollTo(0, saved.y);
}

restoreScrollPosition();

class FacetsToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.backdrop = this.querySelector('.facets__backdrop');
    this.panel = document.getElementById(this.button.getAttribute('aria-controls'));
    if (!this.panel) return;

    this.button.addEventListener('click', () => this.open());
    this.backdrop?.addEventListener('click', () => this.close());
    this.panel.addEventListener('click', (event) => {
      if (event.target.closest('[data-facets-close]')) this.close();
    });

    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.panel.classList.contains('is-open')) this.close();
    };
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
  }

  open() {
    this.panel.classList.add('is-open');
    this.button.setAttribute('aria-expanded', 'true');
    if (this.backdrop) this.backdrop.hidden = false;
    document.body.classList.add('drawer-open');
    this.panel.querySelector('[data-facets-close]')?.focus();
  }

  close() {
    this.panel.classList.remove('is-open');
    this.button.setAttribute('aria-expanded', 'false');
    if (this.backdrop) this.backdrop.hidden = true;
    document.body.classList.remove('drawer-open');
    this.button.focus();
  }
}

class AutoSubmit extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => {
      if (event.target.matches('input[type="number"], input[type="text"], input[type="search"]')) return;
      keepScrollPosition();
      showPageLoader();
      this.querySelector('form')?.submit();
    });

    this.addEventListener('submit', () => {
      keepScrollPosition();
      showPageLoader();
    });
  }
}

class FacetLinks extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => {
      const input = event.target;
      const url = input.checked ? input.dataset.urlAdd : input.dataset.urlRemove;
      if (!url) return;
      keepScrollPosition();
      showPageLoader();
      window.location.href = url;
    });
  }
}

class FacetValues extends HTMLElement {
  connectedCallback() {
    this.limit = Number(this.dataset.limit || 10);
    this.items = [...this.querySelectorAll('[data-facet-value]')];
    this.search = this.querySelector('[data-facet-search]');
    this.more = this.querySelector('[data-facet-more]');
    this.empty = this.querySelector('[data-facet-empty]');
    if (!this.items.length) return;

    this.query = '';
    this.expanded = this.items.some(
      (item, index) => index >= this.limit && item.querySelector('input')?.checked
    );

    this.search?.addEventListener('input', () => {
      this.query = this.search.value.trim().toLowerCase();
      this.paint();
    });

    this.more?.addEventListener('click', () => {
      this.expanded = !this.expanded;
      this.paint();
    });

    this.paint();
  }

  paint() {
    let shown = 0;

    this.items.forEach((item) => {
      const matches = !this.query || item.dataset.facetValue.includes(this.query);
      if (!matches) {
        item.hidden = true;
        return;
      }
      shown += 1;
      const checked = item.querySelector('input')?.checked;
      item.hidden = !this.query && !this.expanded && !checked && shown > this.limit;
    });

    if (this.empty) this.empty.hidden = shown > 0;

    if (this.more) {
      this.more.hidden = Boolean(this.query) || shown <= this.limit;
      this.more.textContent = this.expanded ? this.more.dataset.less : this.more.dataset.more;
    }
  }
}

class SlideShow extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.index = 0;
    if (this.slides.length < 2) return;

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.go(this.index + 1);
      if (event.key === 'ArrowLeft') this.go(this.index - 1);
    });

    this.addEventListener('pointerenter', () => this.stop());
    this.addEventListener('pointerleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());

    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  get interval() {
    return Number(this.dataset.interval || 0);
  }

  start() {
    this.stop();
    if (!this.interval) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.timer = setInterval(() => this.go(this.index + 1), this.interval);
  }

  stop() {
    clearInterval(this.timer);
  }

  go(next) {
    const total = this.slides.length;
    this.index = (next + total) % total;

    this.slides.forEach((slide, i) => {
      const current = i === this.index;
      slide.classList.toggle('is-active', current);
      slide.setAttribute('aria-hidden', String(!current));
      slide.querySelectorAll('a, button').forEach((el) => {
        if (current) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    });

    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
  }
}

class ProductCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    if (!this.track) return;

    this.prev?.addEventListener('click', () => this.scrollByPage(-1));
    this.next?.addEventListener('click', () => this.scrollByPage(1));
    this.track.addEventListener('scroll', () => this.sync(), { passive: true });
    new ResizeObserver(() => this.sync()).observe(this.track);
    this.sync();
  }

  scrollByPage(direction) {
    const item = this.track.querySelector(':scope > *');
    if (!item) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = (item.getBoundingClientRect().width + gap) * this.perView;
    this.track.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  get perView() {
    const item = this.track.querySelector(':scope > *');
    if (!item) return 1;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const width = item.getBoundingClientRect().width + gap;
    return Math.max(1, Math.round(this.track.clientWidth / width));
  }

  sync() {
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    const atStart = this.track.scrollLeft <= 2;
    const atEnd = this.track.scrollLeft >= max;
    const overflows = max > 2;

    if (this.prev) this.prev.disabled = atStart || !overflows;
    if (this.next) this.next.disabled = atEnd || !overflows;
    this.classList.toggle('is-static', !overflows);
  }
}

class TabGroup extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[data-tab]')];
    this.panels = [...this.querySelectorAll('[data-panel]')];
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab.dataset.tab));
      tab.addEventListener('keydown', (event) => {
        const index = this.tabs.indexOf(tab);
        if (event.key === 'ArrowRight') this.focusTab(index + 1);
        if (event.key === 'ArrowLeft') this.focusTab(index - 1);
      });
    });
  }

  focusTab(index) {
    const tab = this.tabs[(index + this.tabs.length) % this.tabs.length];
    tab.focus();
    this.select(tab.dataset.tab);
  }

  select(id) {
    this.tabs.forEach((tab) => {
      const current = tab.dataset.tab === id;
      tab.setAttribute('aria-selected', String(current));
      tab.tabIndex = current ? 0 : -1;
    });
    this.panels.forEach((panel) => {
      const current = panel.dataset.panel === id;
      panel.hidden = !current;
      if (current) panel.querySelector('product-carousel')?.sync();
    });
  }
}

class PriceRange extends HTMLElement {
  connectedCallback() {
    this.max = Number(this.dataset.max || 0);
    this.rangeMin = this.querySelector('[data-range-min]');
    this.rangeMax = this.querySelector('[data-range-max]');
    this.numberMin = this.querySelector('[data-number-min]');
    this.numberMax = this.querySelector('[data-number-max]');
    this.fill = this.querySelector('[data-range-fill]');
    if (!this.rangeMin || !this.rangeMax) return;

    this.rangeMin.addEventListener('input', () => this.fromSlider('min'));
    this.rangeMax.addEventListener('input', () => this.fromSlider('max'));
    this.numberMin.addEventListener('input', () => this.fromNumber());
    this.numberMax.addEventListener('input', () => this.fromNumber());
    this.paint();
  }

  fromSlider(which) {
    let min = Number(this.rangeMin.value);
    let max = Number(this.rangeMax.value);

    if (min > max) {
      if (which === 'min') min = max;
      else max = min;
      this.rangeMin.value = min;
      this.rangeMax.value = max;
    }

    this.numberMin.value = min > 0 ? min : '';
    this.numberMax.value = max < this.max ? max : '';
    this.paint();
  }

  fromNumber() {
    const min = this.numberMin.value === '' ? 0 : Number(this.numberMin.value);
    const max = this.numberMax.value === '' ? this.max : Number(this.numberMax.value);
    this.rangeMin.value = Math.min(Math.max(min, 0), this.max);
    this.rangeMax.value = Math.min(Math.max(max, 0), this.max);
    this.paint();
  }

  paint() {
    if (!this.fill || !this.max) return;
    const min = (Number(this.rangeMin.value) / this.max) * 100;
    const max = (Number(this.rangeMax.value) / this.max) * 100;
    this.fill.style.insetInlineStart = `${min}%`;
    this.fill.style.insetInlineEnd = `${100 - max}%`;
  }
}

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.dialog = document.querySelector('[data-lightbox]');
    if (!this.dialog) return;

    this.image = this.dialog.querySelector('[data-lightbox-image]');
    this.count = this.dialog.querySelector('[data-lightbox-count]');

    const data = this.querySelector('[data-gallery]');
    try {
      this.slides = data ? JSON.parse(data.textContent) : [];
    } catch {
      this.slides = [];
    }
    if (!this.slides.length) return;
    this.index = 0;

    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-zoom]');
      if (!trigger) return;
      this.open(Number(trigger.dataset.index || 0));
    });

    this.dialog.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.show(this.index - 1));
    this.dialog.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.show(this.index + 1));

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.show(this.index + 1);
      if (event.key === 'ArrowLeft') this.show(this.index - 1);
    });

    this.image.addEventListener('click', () => this.image.classList.toggle('is-zoomed'));
    this.dialog.addEventListener('close', () => this.image.classList.remove('is-zoomed'));

    this.hoverZoom();
    this.stageSlider();
  }

  stageSlider() {
    this.stage = this.querySelector('[data-stage]');
    this.dots = [...this.querySelectorAll('[data-stage-dot]')];
    if (!this.stage || this.dots.length < 2) return;

    const itemWidth = () => {
      const item = this.stage.querySelector('.product__media');
      return item ? item.getBoundingClientRect().width : 0;
    };

    const current = () => {
      const width = itemWidth();
      return width ? Math.round(this.stage.scrollLeft / width) : 0;
    };

    const paintDots = () => {
      const active = current();
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === active)));
    };

    const goTo = (index) => {
      const width = itemWidth();
      const max = this.dots.length - 1;
      const target = Math.min(Math.max(index, 0), max);
      this.stage.scrollTo({ left: target * width, behavior: 'auto' });
      paintDots();
    };

    this.querySelector('[data-stage-prev]')?.addEventListener('click', () => goTo(current() - 1));
    this.querySelector('[data-stage-next]')?.addEventListener('click', () => goTo(current() + 1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => goTo(i)));
    this.stage.addEventListener('scroll', paintDots, { passive: true });
  }

  hoverZoom() {
    if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    this.querySelectorAll('.product__media').forEach((frame) => {
      const img = frame.querySelector('img');
      if (!img) return;

      let timer;

      frame.addEventListener('pointerenter', () => {
        clearTimeout(timer);
        timer = setTimeout(() => frame.classList.add('is-zooming'), 260);
      });

      frame.addEventListener('pointerleave', () => {
        clearTimeout(timer);
        frame.classList.remove('is-zooming');
        img.style.transformOrigin = '';
      });

      frame.addEventListener('pointermove', (event) => {
        const box = frame.getBoundingClientRect();
        const x = ((event.clientX - box.left) / box.width) * 100;
        const y = ((event.clientY - box.top) / box.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
      });
    });
  }

  open(index) {
    this.show(index);
    this.dialog.showModal();
  }

  show(next) {
    const total = this.slides.length;
    if (!total) return;
    this.index = (next + total) % total;
    const slide = this.slides[this.index];
    this.image.classList.remove('is-zoomed');
    this.image.src = slide.src;
    this.image.alt = slide.alt;
    if (this.count) this.count.textContent = total > 1 ? `${this.index + 1} / ${total}` : '';
    this.dialog.classList.toggle('lightbox--single', total < 2);
  }
}

class ProductBar {
  static init() {
    const bar = document.querySelector('[data-product-bar]');
    const form = document.querySelector('product-form form');
    if (!bar || !form) return;

    const anchor = document.querySelector('.product__buy');
    if (!anchor) return;

    bar.querySelector('[data-bar-add]')?.addEventListener('click', () => {
      form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true }));
    });

    const mobile = matchMedia('(max-width: 899px)');

    const observer = new IntersectionObserver(([entry]) => {
      if (mobile.matches) return;
      bar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
    });

    const apply = () => {
      if (mobile.matches) {
        bar.hidden = false;
        observer.unobserve(anchor);
      } else {
        bar.hidden = true;
        observer.observe(anchor);
      }
    };

    mobile.addEventListener('change', apply);
    apply();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => ProductBar.init());
} else {
  ProductBar.init();
}

class QuickSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.panel = this.querySelector('[data-results]');
    this.timer = null;
    this.controller = null;
    this.term = '';

    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', (event) => {
      this.input.value = this.input.value.trim();
      if (this.input.value) return;
      event.preventDefault();
      this.close();
      this.input.focus();
    });

    this.input.addEventListener('input', () => this.schedule());
    this.input.addEventListener('focus', () => {
      if (this.panel.innerHTML.trim()) this.open();
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.close();
        this.input.blur();
      }
    });

    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.close();
    });
  }

  schedule() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.search(), 250);
  }

  async search() {
    const term = this.input.value.trim();
    if (term === this.term) return;
    this.term = term;

    if (term.length < 2) {
      this.close();
      this.panel.innerHTML = '';
      return;
    }

    this.controller?.abort();
    this.controller = new AbortController();
    this.setAttribute('aria-busy', 'true');

    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product,collection,page,article',
      'resources[limit]': '8',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search',
    });

    try {
      const response = await fetch(`${routes}search/suggest?${params}`, { signal: this.controller.signal });
      if (!response.ok) throw new Error(response.status);

      this.panel.innerHTML = await response.text();
      this.panel.innerHTML.trim() ? this.open() : this.close();
    } catch (error) {
      if (error.name !== 'AbortError') this.close();
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  open() {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }
}

customElements.define('quick-search', QuickSearch);
customElements.define('product-gallery', ProductGallery);
customElements.define('price-range', PriceRange);
customElements.define('product-carousel', ProductCarousel);
customElements.define('tab-group', TabGroup);
customElements.define('slide-show', SlideShow);
customElements.define('cart-drawer', CartDrawer);
customElements.define('cart-toggle', CartToggle);
customElements.define('product-form', ProductForm);
customElements.define('variant-picker', VariantPicker);
customElements.define('quantity-input', QuantityInput);
customElements.define('menu-toggle', MenuToggle);
customElements.define('auto-submit', AutoSubmit);
customElements.define('facets-toggle', FacetsToggle);
customElements.define('cart-form', CartForm);
customElements.define('facet-links', FacetLinks);
customElements.define('facet-values', FacetValues);

const header = document.querySelector('.header');

if (header) {
  let ticking = false;

  const setCompact = () => {
    const compact = header.classList.contains('is-compact');
    const y = window.scrollY;
    if (!compact && y > 160) header.classList.add('is-compact');
    if (compact && y < 60) header.classList.remove('is-compact');
    ticking = false;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(setCompact);
    },
    { passive: true }
  );

  setCompact();

  const group = header.closest('.shopify-section-group-header-group');
  const utility = header.querySelector('.header__utility');

  const measureUtility = () => {
    if (!group) return;
    group.style.setProperty('--header-utility', `${utility ? utility.offsetHeight : 0}px`);
  };

  measureUtility();
  window.addEventListener('resize', measureUtility);

  header.querySelector('[data-header-search]')?.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    header.classList.remove('is-compact');
    header.querySelector('#header-search')?.focus();
  });
}

document.querySelectorAll('input[data-suggestions]').forEach((input) => {
  const terms = input.dataset.suggestions
    .split(',')
    .map((term) => term.trim())
    .filter(Boolean);

  if (terms.length === 0) return;

  const prefix = input.dataset.suggestPrefix || '';
  const base = input.placeholder;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const lead = prefix ? `${prefix} ` : '';

  let index = 0;
  let typed = 0;
  let deleting = false;
  let timer = null;
  let running = false;

  const step = () => {
    const term = terms[index];

    if (!deleting && typed < term.length) {
      typed += 1;
    } else if (deleting && typed > 0) {
      typed -= 1;
    }

    input.placeholder = `${lead}${term.slice(0, typed)}|`;

    let delay = deleting ? 45 : 85;

    if (!deleting && typed === term.length) {
      deleting = true;
      delay = 1800;
    } else if (deleting && typed === 0) {
      deleting = false;
      index = (index + 1) % terms.length;
      delay = 400;
    }

    timer = setTimeout(step, delay);
  };

  const start = () => {
    if (running || input.value) return;
    running = true;
    if (reduced) {
      input.placeholder = `${lead}${terms[index]}`;
      return;
    }
    step();
  };

  const stop = () => {
    clearTimeout(timer);
    running = false;
    typed = 0;
    deleting = false;
    input.placeholder = base;
  };

  input.addEventListener('focus', stop);
  input.addEventListener('blur', () => {
    if (!input.value) start();
  });

  start();
});

class ReadMore extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-read-more-content]');
    this.toggle = this.querySelector('[data-read-more-toggle]');
    this.label = this.querySelector('[data-read-more-label]');
    if (!this.content || !this.toggle) return;

    this.toggle.addEventListener('click', () => this.switch());
    this.content.addEventListener('transitionend', (event) => {
      if (event.propertyName !== 'max-height') return;
      this.content.style.maxHeight = this.classList.contains('is-open') ? 'none' : '';
    });

    this.check();
    window.addEventListener('resize', () => this.check());
  }

  switch() {
    const open = !this.classList.contains('is-open');
    this.content.style.maxHeight = `${this.content.scrollHeight}px`;

    if (open) {
      this.classList.add('is-open');
    } else {
      this.content.offsetHeight;
      this.classList.remove('is-open');
      this.content.style.maxHeight = '';
    }

    this.toggle.setAttribute('aria-expanded', String(open));
    if (this.label) this.label.textContent = open ? this.toggle.dataset.less : this.toggle.dataset.more;
  }

  check() {
    if (this.classList.contains('is-open')) return;
    this.classList.remove('is-short');
    const fits = this.content.scrollHeight <= this.content.clientHeight + 4;
    this.classList.toggle('is-short', fits);
    this.toggle.hidden = fits;
  }
}

customElements.define('read-more', ReadMore);
